import { DimensionDetails } from "../../interfaces/CoObject";
import { DimensionItem } from "./TreeAmountSelector";
import React, { useState } from "react";
import { MultiSelectPills } from "../../generic/MultiSelectPills";
import { checkBeforeDelete, ModalSelectDimensionTree } from "./TreeInputModal";
import { Icon } from "@fluentui/react";
import { Toggle } from "@fluentui/react/lib/Toggle";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";

interface TreeDropDownProps {
  label: string;
  placeholder: string;
  onChange: (values: (DimensionDetails | undefined)[]) => void;
  disabled?: boolean;
  rootOid: string;
  multiSelectable: boolean;

  hierarchical: boolean;
  dimensions: (DimensionDetails | undefined)[];
  onRemove?: () => void;
  modalTitle: string;

  fetchRoot: (parentOid: string) => Promise<DimensionItem[]>;
  fetchChildren: (parent: DimensionItem) => Promise<DimensionItem[]>;
  canInherit: DimensionDetails[] | undefined;
  inherited?: boolean;
  dbId?: string;
  isSearchAvailable: boolean;
}

export const TreeDropDownInput = ({
  placeholder,
  label,
  onChange,
  disabled,
  multiSelectable,
  hierarchical,
  rootOid,
  dimensions,
  onRemove,
  modalTitle,
  fetchRoot,
  fetchChildren,
  canInherit,
  inherited,
  dbId,
  isSearchAvailable,
}: TreeDropDownProps) => {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", paddingTop: "5" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px" }}>
        <span className="bodyText">{label}</span>
        {onRemove ? <span className="k-icon k-i-close" onClick={onRemove} /> : null}
      </div>
      {canInherit ? (
        <div style={{ justifyContent: "space-between", alignItems: "center" }}>
          <Toggle
            label={formatMessage("cof.documentproperties.inherit")}
            inlineLabel
            checked={inherited ?? null}
            onChange={(_, checked) => {
              if (checked) onChange(canInherit.map((v) => ({ ...v, inherited: true })));
              else onChange([]);
            }}
            styles={{ root: { marginBottom: 0 } }}
            disabled={(!canInherit.length && !inherited) || disabled}
            onText=" "
            offText=" "
          />
        </div>
      ) : null}
      <div
        style={{
          border: "1px solid var(--color-black)",
          borderRadius: "4px",
          display: "flex",
          paddingLeft: "0.25rem",
          paddingBottom: "5",
          backgroundColor: "white",
        }}
      >
        <MultiSelectPills
          id={modalTitle}
          placeholder={placeholder}
          onDelete={(valueToDelete) => {
            const newDimensions = checkBeforeDelete(dimensions, valueToDelete);
            onChange(newDimensions);
          }}
          onClick={() => !inherited && !disabled && setModalOpen(true)}
          disabled={disabled || inherited}
          values={dimensions}
        />
        {inherited || disabled ? null : (
          <Icon
            data-testid="More"
            id={modalTitle}
            iconName="More"
            style={{ margin: "auto 0.5rem", cursor: "pointer" }}
            onClick={() => setModalOpen(true)}
          />
        )}
      </div>
      {!disabled && isModalOpen ? (
        <ModalSelectDimensionTree
          onlyOneValue={multiSelectable === false}
          isModalOpen={true}
          setModalOpen={setModalOpen}
          onChange={(newDimensions) => {
            onChange(newDimensions);
          }}
          fetchRoot={fetchRoot}
          fetchChildren={fetchChildren}
          dimensions={
            dimensions
              ? dimensions.map((d) => ({
                  //cloning values, needed for keeping changes unsaved
                  values: d.values.map((v) => ({ ...v })),
                  hierarchy: d.hierarchy,
                  dimensionOid: d.dimensionOid,
                  inherited: d.inherited,
                  label: d.label,
                }))
              : undefined
          }
          title={modalTitle}
          rootOid={rootOid}
          hierarchical={hierarchical}
          dbId={dbId}
          isSearchAvailable={isSearchAvailable}
        />
      ) : null}
    </div>
  );
};
